/* eslint-disable @typescript-eslint/no-unused-vars */
import { Badge, Box, Flex, HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { IScoreItem } from '../../../../services/hooks/CandidateArea/useGetResultByStep';
import { TextXS, TextXXS } from '../../../../components/Text/Index';
import { useState } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';

interface IOwnProps {
  scoreItem: IScoreItem;
}

interface ICriteria {
  code: string;
  score: string;
  name: string;
}

const ScoreItemComponent: React.FC<IOwnProps> = ({ scoreItem }) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasDetails = scoreItem?.details && Object.keys(scoreItem.details).length > 0;
  const questionsCount = hasDetails ? Object.keys(scoreItem.details).length : 0;

  const isDiscountPoints = scoreItem.correction_mode == 'discount';

  const formatQuestionKey = (key: string): string => {
    // Converte "q1" para "Questão 1"
    const match = key.toLowerCase().match(/q(\d+)/);
    if (match) {
      return `Questão ${parseInt(match[1])}`;
    }

    // Se não for no formato esperado, capitaliza a primeira letra
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  return (
    <Box>
      <Stack
        p={3}
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.100"
        boxShadow="sm"
        transition="all 0.2s"
        _hover={{ boxShadow: 'md' }}
        justify={'space-between'}
        bg={'white'}
      >
        <Flex onClick={() => setIsOpen(!isOpen)} cursor={'pointer'} justify={'space-between'}>
          <TextXS display="flex" alignItems="center" gap={2} fontWeight={'bold'} color="gray.700">
            {scoreItem.label.toLowerCase() == 'prova' ? 'Nota final' : scoreItem.label}
          </TextXS>
          <Flex gap={2}>
            <Badge
              colorScheme="blue"
              variant="subtle"
              px={2}
              py={1}
              borderRadius="md"
              fontSize="xs"
              mr={scoreItem.label.toLowerCase() == 'prova' ? 5 : 0}
            >
              {scoreItem.points.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pontos
            </Badge>
            {scoreItem.label.toLowerCase() !== 'prova' && (
              <TextXXS
                mb={scoreItem.label.toLowerCase() == 'prova' ? '100px' : ''}
                onClick={() => setIsOpen(!isOpen)}
                cursor="pointer"
                color="blue.600"
                fontWeight="medium"
                _hover={{ color: 'blue.700' }}
                transition="color 0.2s"
                display="inline-flex"
                alignItems="center"
                gap={1}
              >
                {isOpen ? <FaAngleUp /> : <FaAngleDown />}
              </TextXXS>
            )}
          </Flex>
        </Flex>

        {hasDetails && scoreItem.label.toLowerCase() !== 'prova' && isOpen && (
          <Box mt={3} p={3} bg="gray.50" borderRadius="lg" border="1px" borderColor="gray.200">
            {isDiscountPoints && (
              <Flex mb={1}>
                <Badge px={2} h={'fit-content'} w={'fit-content'} colorScheme={isDiscountPoints ? 'red' : 'red'}>
                  Desconto de pontos
                </Badge>
              </Flex>
            )}
            <TextXS fontWeight="bold" color="gray.700" mb={3}>
              Detalhamento por questão {questionsCount > 1 ? `(${questionsCount} questões)` : ''}
            </TextXS>
            <Box display={'flex'} flexDir={'column'} gap={3}>
              {Object.entries(scoreItem.details).map(([questionKey, criteriaList]) => {
                const questionTotal =
                  Array.isArray(criteriaList) &&
                  (criteriaList && (criteriaList as ICriteria[]))?.reduce(
                    (sum, criteria) => sum + parseFloat(criteria?.score?.replace(',', '.')),
                    0,
                  );

                if (!Array.isArray(criteriaList)) {
                  return (
                    <Box key={Math.random()}>
                      <HStack justify="space-between" py={3} borderBottom="1px solid" borderColor="gray.100">
                        <Text fontWeight="medium" color="gray.700">
                          {formatQuestionKey(questionKey)}
                        </Text>
                        <Text fontWeight="bold" color="blue.600">
                          {String(criteriaList)} pts
                        </Text>
                      </HStack>
                    </Box>
                  );
                }

                return (
                  <Box key={questionKey} bg="white" borderRadius="md" p={3} border="1px" borderColor="gray.200">
                    {/* Header da questão */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                      pb={2}
                      borderBottom="1px"
                      borderColor="gray.100"
                    >
                      <TextXXS fontWeight="bold">
                        {scoreItem.type === 'dissertative' ? 'Questão' : scoreItem.type === 'essay' ? 'Redação' : ''}{' '}
                        {scoreItem.type === 'dissertative' ? questionKey?.replace('q', '') : ''}
                      </TextXXS>
                      <Flex>
                        <TextXXS color={isDiscountPoints ? 'red.500' : 'blue.500'} fontWeight="bold">
                          {questionTotal?.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                          pts
                        </TextXXS>
                      </Flex>
                    </Box>

                    {/* Critérios em grid compacto */}
                    <SimpleGrid columns={1} spacing={2}>
                      {(criteriaList as ICriteria[]).map((criteria, index) => (
                        <Box key={index} display="flex" justifyContent={'space-between'} gap={1}>
                          <TextXXS color="gray.600">{criteria.name}:</TextXXS>
                          <TextXXS fontWeight="bold" color="gray.700">
                            {criteria.score.replace(/\./g, ',')}
                          </TextXXS>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                );
              })}
            </Box>

            {/* Total geral apenas se tiver múltiplas questões */}
            {questionsCount > 1 && (
              <Box mt={3} pt={3} borderTop="1px" borderColor="gray.300" bg="blue.50" borderRadius="md" p={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <TextXXS fontWeight="bold" color="gray.700">
                    Total {isDiscountPoints ? 'descontado' : 'geral'}:
                  </TextXXS>
                  <TextXXS fontWeight="bold" color={isDiscountPoints ? 'red.600' : 'green.600'}>
                    {Object.entries(scoreItem.details)
                      .reduce((total, [_, criteriaList]) => {
                        const isArray = Array.isArray(criteriaList);

                        if (isArray) {
                          const questionTotal = (criteriaList as ICriteria[])?.reduce(
                            (sum, criteria) => sum + parseFloat(criteria?.score?.replace(',', '.')),
                            0,
                          );
                          return total + questionTotal;
                        } else {
                          const numericValue =
                            typeof criteriaList === 'number'
                              ? criteriaList
                              : parseFloat(String(criteriaList).replace(',', '.') || '0');
                          return total + numericValue;
                        }
                      }, 0)
                      .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                    pontos
                  </TextXXS>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ScoreItemComponent;
